import React, { useEffect, useRef, useState } from 'react'
import { formaterDate } from '../functions/dateUtils.js'
import { api, apiPut } from '../functions/api.js'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import 'ckeditor5/ckeditor5.css'
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Mention,
  Paragraph,
  Undo,
  List,
  Underline,
  Strikethrough,
  Highlight,
  Alignment,
  Link,
  Font,
} from 'ckeditor5'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { DatePicker, Select, Input, Popconfirm } from 'antd'
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { useCookies } from 'react-cookie'
import dayjs from 'dayjs'
import locale from 'antd/locale/fr_BE'
import 'dayjs/locale/fr.js'
import { toast } from 'react-toastify'

dayjs.locale('fr_BE')
const dateFormat = 'DD/MM/YYYY'

const PriorityNoteCarnetDeLiaison = ({ note, jeunes, cours, profs }) => {
  const divRef = useRef(null)
  const [deleteNote, setDeleteNote] = useState(false)
  const [edit, setEdit] = useState(false)
  const [noteId, setNoteId] = useState(note.id)
  const [commentaire, setCommentaire] = useState(note.commentaire)
  const [intitule, setIntitule] = useState(note.intitule)
  const [elevesId, setElevesId] = useState(JSON.parse(note.eleves_id))
  const [coursId, setCoursId] = useState(JSON.parse(note.cours_id))
  const [date, setDate] = useState(note.date)
  const [profId, setProfId] = useState(note.prof_id)
  const [cookies] = useCookies(['id', 'token'])
  const [vueParMoi, setVueParMoi] = useState(
    Array.isArray(note.vues) && note.vues.includes(parseInt(cookies.id)) // Initialisé si déjà vu
  )

  const formDataInitial = {
    id: noteId,
    date: date,
    prof: profId,
    jeunes: JSON.stringify(elevesId),
    contexte: JSON.stringify(coursId),
    intitule: intitule,
    commentaire: commentaire,
  }

  const [formData, setFormData] = useState(formDataInitial)

  const findEleveById = (id) => jeunes.find((eleve) => eleve.value === parseInt(id))?.label || null
  const findCoursById = (id) => cours.find((c) => c.value === parseInt(id))?.label || null
  const findProfById = (id) => profs.find((prof) => prof.value === parseInt(id))?.label || null

  const generatePdf = () => {
    let nomPdf = `note_de_${findEleveById(elevesId[0])}_du_${date}`.replace(/ /g, '_')
    html2canvas(divRef.current).then((canvas) => {
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF()
      const imgWidth = pdf.internal.pageSize.getWidth()
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(nomPdf + '.pdf')
    })
  }

  const sendDatasToApi = async () => {
    try {
      const response = await apiPut(
        'carnetliaison/update',
        {
          id: cookies.id,
          token: cookies.token,
          'Content-Type': 'application/json',
        },
        formData
      )
      if (response.status === 200) {
        toast.success(response.data.message)
        setCommentaire(formData.commentaire)
        setIntitule(formData.intitule)
        setElevesId(JSON.parse(formData.jeunes))
        setProfId(formData.prof)
        setCoursId(JSON.parse(formData.contexte))
        setDate(formData.date)
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      console.error('Erreur de mise à jour :', error)
    }
  }

  const supprimerNote = async () => {
    try {
      const response = await api(`carnetliaison/destroy/${noteId}`, {
        id: cookies.id,
        token: cookies.token,
        'Content-Type': 'application/json',
      })
      if (response.status === 200) {
        toast.success(response.data.message)
        setDeleteNote(true)
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      console.error('Erreur de suppression :', error)
    }
  }

  // 🔁 Intersection Observer pour marquer la note comme vue
  useEffect(() => {
    if (!divRef.current || vueParMoi) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting) {
          const timer = setTimeout(() => {
            if (entry.isIntersecting && !vueParMoi) {
              apiPut(
                'carnetliaison/view',
                {
                  id: cookies.id,
                  token: cookies.token,
                  'Content-Type': 'application/json',
                },
                {
                  note_id: noteId,
                  prof_id: cookies.id,
                }
              )
                .then((res) => {
                  if (res.status === 200) {
                    setVueParMoi(true)
                    toast.info('Note marquée comme lue')
                  }
                })
                .catch((err) => {
                  console.error('Erreur lors du marquage comme lu :', err)
                })
            }
          }, 3000)

          return () => clearTimeout(timer)
        }
      },
      { threshold: 1.0 } // 100% visible
    )

    observer.observe(divRef.current)
    return () => observer.disconnect()
  }, [divRef, vueParMoi, cookies, noteId])

  if (deleteNote) return null

  return !edit ? (
    <div className="PriorityNoteCarnetLiaison" key={note.id} ref={divRef}>
      <div className="noteCarnetLiaison__header">
        <div className="noteCarnetLiaison__header__top">
          {elevesId.map((id, index) => {
            const label = findEleveById(id)
            return label ? (index > 0 ? ', ' : '') + label : null
          })}
        </div>
        <div className="noteCarnetLiaison__header__center">
          {coursId.map((id, index) => {
            const label = findCoursById(id)
            return label ? (index > 0 ? ', ' : '') + label : null
          })}
        </div>
        <div className="noteCarnetLiaison__header__bottom">par {findProfById(profId)}</div>
      </div>
      <div className="noteCarnetLiaison__body">
        <div className="noteCarnetLiaison__body__header">{intitule}</div>
        <div className="noteCarnetLiaison__body__body" dangerouslySetInnerHTML={{ __html: commentaire }}></div>
      </div>
      <div className="noteCarnetLiaison__footer">
        <div className="noteCarnetLiaison__footer__left">{formaterDate(date, 'mots')}</div>
        <div className="noteCarnetLiaison__footer__right">
          <FilePdfOutlined onClick={generatePdf} />
          <EditOutlined onClick={() => setEdit(true)} />
          <Popconfirm
            title="Suppression de la note"
            description="Es-tu sûr de vouloir supprimer la note ?"
            onConfirm={supprimerNote}
            icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
            okText="Oui"
            cancelText="Non"
          >
            <DeleteOutlined />
          </Popconfirm>
        </div>
      </div>
    </div>
  ) : (
    <div className="noteCarnetLiaison" key={note.id} ref={divRef}>
      <div className="noteCarnetLiaison__header">
        <div className="noteCarnetLiaison__header__top">
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            size="large"
            defaultValue={elevesId}
            options={jeunes}
            onChange={(e) => setFormData((prev) => ({ ...prev, jeunes: JSON.stringify(e) }))}
          />
        </div>
        <div className="noteCarnetLiaison__header__center">
          <Select
            style={{ width: '100%', marginTop: 12 }}
            size="large"
            defaultValue={coursId}
            options={cours}
            onChange={(e) => setFormData((prev) => ({ ...prev, contexte: JSON.stringify([e]) }))}
          />
        </div>
        <div className="noteCarnetLiaison__header__bottom">
          <Select
            style={{ width: '100%', marginTop: 12 }}
            size="large"
            defaultValue={profId}
            options={profs}
            onChange={(e) => setFormData((prev) => ({ ...prev, prof: e }))}
          />
        </div>
      </div>
      <div className="noteCarnetLiaison__body">
        <Input
          addonBefore="Contexte"
          defaultValue={intitule}
          size="large"
          style={{ width: '100%' }}
          onChange={(e) => setFormData((prev) => ({ ...prev, intitule: e.target.value }))}
        />
        <div className="commentaire__box" style={{ marginTop: 12 }}>
          <CKEditor
            editor={ClassicEditor}
            config={{
              toolbar: {
                items: [
                  'heading',
                  'fontSize',
                  'bold',
                  'italic',
                  'underline',
                  'strikethrough',
                  'highlight',
                  '|',
                  'bulletedList',
                  'numberedList',
                  '|',
                  'alignment',
                  'link',
                  'imageUpload',
                  'undo',
                  'redo',
                ],
              },
              plugins: [Bold, Italic, Underline, Strikethrough, Font, Highlight, Paragraph, List, Alignment, Link],
              fontSize: {
                options: ['tiny', 'small', 'default', 'big', 'huge'],
              },
              alignment: {
                options: ['left', 'center', 'right', 'justify'],
              },
            }}
            data={commentaire}
            onChange={(event, editor) => {
              const data = editor.getData()
              setFormData((prev) => ({ ...prev, commentaire: data }))
            }}
          />
        </div>
      </div>
      <div className="noteCarnetLiaison__footer">
        <div className="noteCarnetLiaison__footer__left">
          <DatePicker
            style={{ width: '100%' }}
            size="large"
            defaultValue={dayjs(date)}
            format={dateFormat}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                date: dayjs(e).format('YYYY-MM-DD HH:mm:ss'),
              }))
            }
          />
        </div>
        <div className="noteCarnetLiaison__footer__right">
          <CloseOutlined
            onClick={() => {
              setEdit(false)
              setFormData(formDataInitial)
            }}
          />
          <SaveOutlined
            onClick={() => {
              sendDatasToApi()
              setEdit(false)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default PriorityNoteCarnetDeLiaison
